import { Component, input, model } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { DmButtonGroup, DmButtonGroupItem, DmFlag, DmIconDot } from '@digitale-menschen/component-library-angular';

/**
 * How far a language's texts are filled in: everything the caller expects, only part of it, or
 * nothing. The caller decides what counts as which.
 */
export type LanguageTabState = 'complete' | 'partial' | 'empty';

/** One tab of the strip: the language's `spracheLokalisierung` code and how far it is filled in. */
export interface LanguageTab {
  sprache: string;
  state: LanguageTabState;
}

/**
 * The DE | FR | IT | EN switch of a translated form: one tab per language with its flag, its code
 * and a status dot. A read-only view of the texts leaves the dots out, since nothing is filled in.
 *
 * Presentational only. The caller owns the texts, works out each language's
 * {@link LanguageTabState}, and binds the active tab two-way.
 */
@Component({
  selector: 'lp-language-tabs',
  imports: [DmButtonGroup, DmButtonGroupItem, DmFlag, DmIconDot, UpperCasePipe],
  templateUrl: './language-tabs.component.html',
  host: { class: 'lp-language-tabs' },
})
export class LanguageTabsComponent {
  public readonly languages = input.required<LanguageTab[]>();

  /** Whether each tab carries the dot saying how far its language is filled in; off where the texts are only read. */
  public readonly showsState = input<boolean>(true);

  public readonly size = input<'sm' | 'md'>('md');

  /** The position of the active tab in {@link languages}; changes only when the user picks another one. */
  public readonly activeIndex = model<number>(0);

  /** The status dot's colour per state. An untouched language is gray, not red: it is not an error yet. */
  protected readonly dotColorByState: Record<LanguageTabState, 'success' | 'error' | 'gray'> = {
    complete: 'success',
    partial: 'error',
    empty: 'gray',
  };
}
